import { TreinserieDisplay } from "@/features/infra";
import { Badge, Icon } from "@/shared/components";
import { capitalizeFirstChar } from "@/shared/utils";
import { PropsWithChildren } from "react";

type TreinBadgeProps<T = Record<string, unknown>> = T & {
    treinSerieOfNummer: string | number;
};

export function TreinBadge({ treinSerieOfNummer, children }: Readonly<PropsWithChildren<TreinBadgeProps>>) {
    return (
        <Badge asChild className="flex flex-row items-center gap-2" data-testid="trein-badge">
            <li>
                <span className="text-xs font-bold" data-testid="trein-serie-of-nummer">
                    <TreinserieDisplay nummer={treinSerieOfNummer} variant="tooltip" />
                </span>
                {children}
            </li>
        </Badge>
    );
}

type VanNaarProps = TreinBadgeProps<{ van?: string; naar?: string; richting: "ViceVersa" | "Heen" }>;

function VanNaar({ treinSerieOfNummer, van, naar, richting }: VanNaarProps) {
    return (
        <TreinBadge treinSerieOfNummer={treinSerieOfNummer}>
            {van && naar && (
                <p className="flex flex-row items-center gap-1 text-xs">
                    <span>{capitalizeFirstChar(van)}</span>
                    <Icon name={richting === "ViceVersa" ? "arrow-right-left" : "arrow-right"} size="xs" />
                    <span>{capitalizeFirstChar(naar)}</span>
                </p>
            )}
        </TreinBadge>
    );
}
type AanvullendProps = TreinBadgeProps<{ van?: string; naar?: string; type: "StopVerwijderen" | "ExtraStop" | "Omleiding" }>;

function Aanvullend({ treinSerieOfNummer, van, naar, type }: AanvullendProps) {
    if (type === "ExtraStop" || type === "StopVerwijderen") {
        const label = type === "ExtraStop" ? "Extra stop" : "Stopt niet";
        return (
            <TreinBadge treinSerieOfNummer={treinSerieOfNummer}>
                {van && (
                    <p className="flex flex-row items-center gap-1 text-xs">
                        <span className="italic" data-testid="aanvullend-type">
                            {label}
                        </span>
                        <span>{capitalizeFirstChar(van ?? "")}</span>
                        {naar && <span>ri.</span>}
                        {naar && <span>{capitalizeFirstChar(naar ?? "")}</span>}
                    </p>
                )}
            </TreinBadge>
        );
    }

    return (
        <TreinBadge treinSerieOfNummer={treinSerieOfNummer}>
            {van && naar && (
                <p className="flex flex-row items-center gap-1 text-xs">
                    <span className="italic" data-testid="omleiding">
                        Omleiding
                    </span>
                    <span>{capitalizeFirstChar(van)}</span>
                    <Icon name="arrow-right" size="xs" />
                    <span>{capitalizeFirstChar(naar)}</span>
                </p>
            )}
        </TreinBadge>
    );
}

TreinBadge.VanNaar = VanNaar;
TreinBadge.Aanvullend = Aanvullend;
